import { msg } from "@lingui/core/macro";
import { Show } from "solid-js";
import { Dialog, DialogDescription, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useConnectivityDialog } from "@/hooks/use-connectivity-dialog";
import { fromActorRef } from "@xstate/solid";
import { useNavigate } from "@/lib/u-router";

interface ConnectivityModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ConnectivityModal(props: ConnectivityModalProps) {
  const { connectivityRef } = usePowerSyncContext();
  const snapshot = fromActorRef(connectivityRef);
  const { t } = useI18n();
  const navigate = useNavigate();

  const stateValue = () => snapshot().value;
  const context = () => snapshot().context;

  const dialogContent = useConnectivityDialog(stateValue, context);

  const handleConfirm = () => {
    const content = dialogContent();
    props.onClose();
    if (content.actionType === "auth") {
      void navigate({ to: "/login" });
    } else {
      connectivityRef.send({ type: "RETRY" });
    }
  };

  const handleSecondaryConfirm = () => {
    const content = dialogContent();
    props.onClose();
    if (content.actionType === "auth") {
      void navigate({ to: "/register" });
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          props.onClose();
        }
      }}
      overlayClass=""
      contentClass="max-w-[400px] p-6"
    >
      <DialogTitle class="text-xl font-bold mb-2">{dialogContent().title}</DialogTitle>
      <DialogDescription class="text-sm text-muted-foreground mb-6">
        {dialogContent().message}
      </DialogDescription>
      <div class="flex flex-col gap-2">
        <Show when={!dialogContent().hideConfirm}>
          <Button
            variant="default"
            class="h-11 rounded-xl font-bold w-full"
            onClick={handleConfirm}
          >
            {dialogContent().confirmText}
          </Button>
        </Show>

        <Show when={dialogContent().secondaryConfirmText}>
          {(text) => (
            <Button
              variant="outline"
              class="h-11 rounded-xl font-bold w-full"
              onClick={handleSecondaryConfirm}
            >
              {text()}
            </Button>
          )}
        </Show>

        <Button variant="ghost" class="h-11 rounded-xl font-bold w-full" onClick={props.onClose}>
          {dialogContent().cancelText ?? t(msg`Close`)}
        </Button>
      </div>
    </Dialog>
  );
}
